/**
 * NativeWidgetRuntime — the web half of the ViewIR runtime contract (§7).
 *
 * Renders a widget from its declarative blueprint over a feed, with NO widget
 * HTML anywhere in the path: blueprint → render context → kit components.
 * It satisfies the same host-callback surface as the WebView renderer, so it
 * can drop into the existing `WidgetRuntimeSurface` branch unchanged.
 *
 * Props
 *   widgetId        the widget's slug (selects the mock feed + the blueprint)
 *   blueprint       a parsed `<widget>.view.json` (required)
 *   feed            explicit host payload `{ entries, settings, pending }`;
 *                   omit to let the runtime source it from `dataMode`
 *   pageId          which page to render — defaults to the `entry: true` one
 *   pageArgs        host-supplied args for that page (item_id, session_id, …)
 *   appearance      'light' | 'dark'
 *   dataMode        'mock' (studio sample feed) | 'user' (the signed-in user's
 *                   real widget instance, when the widget is adopted)
 *   host            the host bridge object; the studio passes a mock
 *   reloadKey       bump to refetch the user feed — the host wrote a row (the
 *                   studio capture button) and the page must show it. No-op in mock mode.
 *   pendingAdds     host-owned in-flight captures merged into the feed's
 *                   `pending.adds`, so a capture that is still running draws
 *                   the kit's real skeleton placeholder (app parity: the app
 *                   pushes the same list while a session runs).
 *   interaction     an OPTIONAL host-owned `{ slotState, dispatch, ui, setUi }`
 *                   (see viewir/slots.js) — the studio strip hands the SAME one
 *                   to every card so they are one app in one state. Omitted
 *                   (the app), the page owns its own inbox as before.
 *   inspect         studio inspect mode `{ dim: Set<nodeId> }` — marks every
 *                   node with its blueprint id and grays the given subtrees.
 *                   Omitted (the app, and the studio's own live preview), the
 *                   render is unchanged.
 *   topInset        px the host's floating chrome (the back button) occupies.
 *                   The page starts BELOW it rather than under it — the app
 *                   does the same by adding `_hostBackButtonHeight` to the
 *                   safe-area padding it hands the interpreter.
 *   targetBuild     render AS this app build (§4.5 graceful degradation): a
 *                   component introduced after it degrades to its declared
 *                   fallback, so an author can see what an older app shows.
 *                   null (the app, and the studio's live preview) = latest.
 *   previewVersion  reserved: once `view.json` is baked into published
 *                   bundles this selects WHICH version's blueprint renders.
 *                   Threaded into the context as `preview_version` so a trace
 *                   can say which version produced a frame.
 *
 * LIVE UPDATE (§7.5): a new `feed` re-evaluates selectors and re-renders in
 * place — the component tree is never remounted, so scroll position, filter
 * chips, the open sheet and any local selection survive the push.
 */
import { useEffect, useMemo, useState } from 'react';
import useWidgetUserFeed from './useWidgetUserFeed';
import { serverViewOf, useServerViewFeed } from './serverView';
import { buildRenderContext } from './viewir/context';
import { ViewIRPage } from './viewir/interpreter';
import { buildTokenStyle } from './viewir/tokens';
import { mockFeed, mockPageArgs } from './mock';
import { PageHeaderHostContext, pageHeaderStyle } from './viewir/pageHeaderContext';
import KitKeyframes from './KitKeyframes';
import { a2uiViewToBlueprint } from './a2ui/components';

const EMPTY_FEED = { entries: [], settings: {}, pending: { adds: [], updates: [] } };

const PAGE_FOCUS_KEYS = [
  'item_id', 'entry_id', 'id', 'focus_entry_id', 'log_card_id', 'source_session_id',
  'session_id', 'grid_id', 'photo_url',
];

/**
 * 真实宿主定位优先于 Studio 的 mock 默认条目；两套 focus token 不能同时留在页面参数里。
 */
function runtimePageArgs(widgetId, pageId, supplied) {
  const explicit = supplied && typeof supplied === 'object' ? supplied : {};
  const fallback = { ...mockPageArgs(widgetId, pageId) };
  if (PAGE_FOCUS_KEYS.some((key) => explicit[key] != null && explicit[key] !== '')) {
    for (const key of PAGE_FOCUS_KEYS) delete fallback[key];
  }
  return { ...fallback, ...explicit };
}

export default function NativeWidgetRuntime({
  widgetId,
  blueprint,
  feed: feedProp = null,
  pageId = null,
  pageArgs: pageArgsProp = null,
  appearance = 'light',
  dataMode = 'mock',
  host = null,
  reloadKey = 0,
  pendingAdds = null,
  interaction = null,
  inspect = null,
  topInset = 0,
  headerLeading = null,
  headerActions = [],
  previewVersion = null,
  targetBuild = null,
  onReport = null,
  onTrace = null,
  onDataState = null,
}) {
  // ── the feed ──────────────────────────────────────────────────────────
  // A `data.source: "server_view"` blueprint reads its rows from the server
  // view instead of the widget's user feed (README §4.7); exactly one is live.
  const serverView = serverViewOf(blueprint);
  const userMode = !feedProp && dataMode === 'user';
  const live = useWidgetUserFeed(userMode && !serverView, widgetId, null, reloadKey);
  const viewFeed = useServerViewFeed(userMode && !!serverView, serverView, reloadKey);
  const source = serverView ? viewFeed : live;
  const userFeed = source.feed;
  const dataState = !userMode ? 'ready'
    : source.state === 'error' ? `error: ${source.error}`
      : source.state === 'ready' && !source.feed?.entries?.length ? 'empty' : source.state;

  useEffect(() => { onDataState?.(dataState); }, [dataState, onDataState]);

  const feed = useMemo(() => {
    const base = feedProp || (dataMode === 'user' ? (userFeed || EMPTY_FEED) : mockFeed(widgetId));
    if (!pendingAdds?.length) return base;
    const pending = base.pending || {};
    return { ...base, pending: { ...pending, adds: [...pendingAdds, ...(pending.adds || [])] } };
  }, [feedProp, dataMode, userFeed, widgetId, pendingAdds]);

  // `host.viewAction` / `host.reloadData` on a server-view page are answered by
  // the runtime itself; every other slot still reaches the caller's bridge.
  const { act: viewAct, reload: viewReload } = viewFeed;
  const runtimeHost = useMemo(() => {
    if (!host || !serverView || !userMode) return host;
    return new Proxy(host, {
      get(target, prop, receiver) {
        if (prop === 'viewAction') return (payload) => viewAct(payload || {});
        if (prop === 'reloadData') return () => { viewReload(); };
        return Reflect.get(target, prop, receiver);
      },
    });
  }, [host, serverView, userMode, viewAct, viewReload]);

  // ── page args (page-local state the DATA lane owns: the selected day) ──
  const [pageArgs, setPageArgs] = useState(() => runtimePageArgs(widgetId, pageId, pageArgsProp));
  useEffect(() => {
    setPageArgs(runtimePageArgs(widgetId, pageId, pageArgsProp));
  }, [widgetId, pageId, pageArgsProp]);

  /**
   * IS THE PAGE STILL WAITING FOR ITS FEED? — the fact the render context needs
   * so a data-bound module can draw a skeleton instead of a confident zero.
   *
   * Three conditions, and every one of them is load-bearing:
   *   `dataMode === 'user'`  MOCK MODE IS NEVER LOADING. The studio's sample
   *      feed is synchronous, so a mock card that could reach this state would
   *      shimmer forever — a whole studio of frozen skeletons.
   *   `!feedProp`  an explicit feed IS the answer, whoever passed it.
   *   `dataState === 'loading'`  the fetch above, before it resolves. The other
   *      states are all answers: 'empty' and 'not-adopted' mean the widget
   *      really does hold nothing, and the page must show its authored empty.
   */
  const loading = dataMode === 'user' && !feedProp && dataState === 'loading';

  // ── view_ir branch (DEC-6) ────────────────────────────────────────────────
  // `1` → the existing interpreter (byte-identical, the hard-gate contract).
  // `2` → an a2ui (a2ui/1.0) widget view: reconstruct the equivalent viewIr
  //       blueprint (the exact inverse of the converter) and render it through
  //       the SAME oracle — parity by construction.
  // `>2` or a non-`v1.0` protocolVersion → the WebView fallback (a released app
  //       that cannot render this major falls back to the iframe widget).
  const viewIrMajor = blueprint ? Number(blueprint.view_ir ?? 1) : 1;
  const a2uiUnsupported = !!blueprint && (viewIrMajor > 2
    || (viewIrMajor === 2 && blueprint.protocolVersion != null && blueprint.protocolVersion !== 'v1.0'));
  const effectiveBlueprint = useMemo(() => {
    if (!blueprint) return blueprint;
    if (viewIrMajor === 2 && !a2uiUnsupported) return a2uiViewToBlueprint(blueprint);
    return blueprint;
  }, [blueprint, viewIrMajor, a2uiUnsupported]);

  // A blueprint may pin its appearance (`"appearance": "light" | "dark"`, README §4) —
  // a widget authored in one palette must not flip on a dark system. Same rule as
  // the Flutter kit (native_widget_runtime.dart). Anything else follows the host.
  const pinned = effectiveBlueprint?.appearance;
  const effectiveAppearance = pinned === 'light' || pinned === 'dark' ? pinned : appearance;

  const ctx = useMemo(() => {
    if (!effectiveBlueprint) return null;
    const c = buildRenderContext({ blueprint: effectiveBlueprint, feed, pageArgs, appearance: effectiveAppearance, targetBuild, loading });
    c.preview_version = previewVersion;
    return c;
  }, [effectiveBlueprint, feed, pageArgs, effectiveAppearance, previewVersion, targetBuild, loading]);

  const tokenStyle = useMemo(
    () => buildTokenStyle({ appearance: effectiveAppearance, accent: effectiveBlueprint?.accent }),
    [effectiveAppearance, effectiveBlueprint?.accent],
  );

  if (!blueprint) {
    return (
      <div style={{ padding: 24, fontSize: 13, color: '#8E8E93' }}>
        No blueprint loaded for “{widgetId}”.
      </div>
    );
  }

  if (a2uiUnsupported) {
    // The native kit cannot render this widget major — the app's
    // WidgetRuntimeSurface serves the WebView (iframe) widget instead.
    return (
      <div data-a2ui-fallback={String(blueprint.view_ir)} style={{ padding: 24, fontSize: 13, color: '#8E8E93' }}>
        This widget needs a newer app — showing the web version.
      </div>
    );
  }

  const modernHeader = pageHeaderStyle(blueprint, pageId);
  return (
    <PageHeaderHostContext.Provider value={{ leading: headerLeading, actions: headerActions }}>
    <div style={{ ...tokenStyle, height: '100%', overflow: 'hidden', position: 'relative' }}>
      <KitKeyframes />
      <div
        data-vi-scroll="1"
        style={{
          height: '100%', overflowY: modernHeader ? 'hidden' : 'auto', overflowX: 'hidden', scrollbarWidth: 'none',
          // a phone absorbs its own wheel: at the top or the bottom of this
          // scroller the gesture STOPS here instead of chaining to the page the
          // phone sits on (the studio strip scrolls under the pointer otherwise)
          overscrollBehavior: 'contain',
          paddingTop: modernHeader ? 0 : (topInset || 0), boxSizing: 'border-box',
        }}
      >
        <ViewIRPage
          blueprint={effectiveBlueprint}
          ctx={ctx}
          pageId={pageId}
          host={runtimeHost}
          onPageArgs={(patch) => setPageArgs((prev) => ({ ...prev, ...patch }))}
          onReport={onReport}
          onTrace={onTrace}
          inspect={inspect}
          interaction={interaction}
        />
      </div>
    </div>
    </PageHeaderHostContext.Provider>
  );
}
